import React, { useState, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import {
  CalendarDays, FileDown, Printer, RefreshCw, TrendingUp, TrendingDown,
  DollarSign, Package, ArrowUp, ArrowDown, Minus,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { getReporteSemanalLotes } from '../../lib/api';
import { exportReporteSemanalLotesPDF } from '../../lib/exportUtils';

const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const fmtMoney = (n) =>
  `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtPct = (n) => `${Number(n || 0).toFixed(1)}%`;

/**
 * Chip visual de variación porcentual. Recibe:
 * - pct: número, o null cuando no hay base comparable (nuevo/sin datos previos)
 * - inverso: cuando es true, un aumento se pinta rojo y una baja verde (ej: rotos, costos)
 */
const VarChip = ({ pct, inverso = false, sufijo = '%', esDelta = false }) => {
  if (pct === null || pct === undefined) {
    return (
      <span
        className="inline-flex items-center gap-0.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground"
        title="Sin datos comparables del período anterior"
      >
        <Minus className="w-2.5 h-2.5" /> —
      </span>
    );
  }
  const n = Number(pct);
  const positivo = n > 0;
  const negativo = n < 0;
  const cero = n === 0;
  // "bueno" depende de si es una métrica inversa (costos, rotos)
  const esBueno = cero ? true : inverso ? negativo : positivo;
  const cls = cero
    ? 'bg-muted text-muted-foreground'
    : esBueno
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300'
      : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
  const Icon = cero ? Minus : positivo ? ArrowUp : ArrowDown;
  const signo = positivo ? '+' : '';
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-0.5 rounded tabular-nums ${cls}`}
      title={esDelta ? 'Diferencia en puntos porcentuales' : 'Variación vs. período anterior'}
    >
      <Icon className="w-2.5 h-2.5" />
      {signo}{Number(n).toFixed(esDelta ? 1 : 1)}{esDelta ? ' pp' : sufijo}
    </span>
  );
};

/**
 * Devuelve el par [lunes, domingo] de la semana ISO que contiene la fecha dada.
 * Formato YYYY-MM-DD.
 */
const getSemanaISO = (baseDate = new Date()) => {
  const d = new Date(baseDate);
  const day = d.getDay(); // 0=domingo … 6=sábado
  // Lunes = 1: si hoy es domingo (0) → restar 6, si es lunes → 0, etc.
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const toISO = (x) => x.toISOString().split('T')[0];
  return [toISO(monday), toISO(sunday)];
};

const shiftSemana = (desde, hasta, offsetDays) => {
  const d1 = new Date(desde);
  const d2 = new Date(hasta);
  d1.setDate(d1.getDate() + offsetDays);
  d2.setDate(d2.getDate() + offsetDays);
  const toISO = (x) => x.toISOString().split('T')[0];
  return [toISO(d1), toISO(d2)];
};

const formatLoteLabel = (lote) => {
  const code = String(lote?.numero_lote || lote?.codigo || lote?.nombre || '').trim();
  if (!code) return `Lote ${(lote?.id || '').slice(0, 6)}`;
  if (/^lote\b/i.test(code)) return code;
  return `Lote ${code}`;
};

/**
 * Reporte Semanal por Lote:
 * - Selector de rango (con presets Esta / Anterior / Personalizado).
 * - Vista previa en pantalla (KPIs, tabla resumen, desglose por tipo).
 * - Botones "Descargar PDF" e "Imprimir".
 */
export const ReporteSemanalLotes = () => {
  const [inicialLunes, inicialDomingo] = useMemo(() => getSemanaISO(new Date()), []);
  const [desde, setDesde] = useState(inicialLunes);
  const [hasta, setHasta] = useState(inicialDomingo);
  const [comparar, setComparar] = useState(true);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generar = useCallback(async () => {
    if (!desde || !hasta) {
      toast.error('Selecciona ambas fechas del período');
      return;
    }
    if (desde > hasta) {
      toast.error('"Desde" no puede ser mayor que "Hasta"');
      return;
    }
    setLoading(true);
    try {
      const res = await getReporteSemanalLotes(desde, hasta, comparar);
      setData(res.data);
      const cnt = res.data?.lotes?.length || 0;
      if (cnt === 0) toast.info('No hay actividad en el período seleccionado');
      else toast.success(`Reporte generado: ${cnt} lote${cnt === 1 ? '' : 's'} con actividad`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar el reporte');
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, comparar]);

  const presetEsta = () => {
    const [d, h] = getSemanaISO(new Date());
    setDesde(d); setHasta(h);
  };
  const presetAnterior = () => {
    const [d, h] = getSemanaISO(new Date());
    const [d2, h2] = shiftSemana(d, h, -7);
    setDesde(d2); setHasta(h2);
  };
  const semanaAnteriorAlActual = () => {
    const [d, h] = shiftSemana(desde, hasta, -7);
    setDesde(d); setHasta(h);
  };
  const semanaSiguienteAlActual = () => {
    const [d, h] = shiftSemana(desde, hasta, 7);
    setDesde(d); setHasta(h);
  };

  const descargarPDF = () => {
    if (!data || data.lotes.length === 0) {
      toast.error('Primero genera el reporte y asegúrate que tenga datos');
      return;
    }
    try {
      exportReporteSemanalLotesPDF(data, 'save');
      toast.success('PDF descargado');
    } catch (e) {
      toast.error('Error al generar el PDF');
    }
  };

  const imprimirPDF = () => {
    if (!data || data.lotes.length === 0) {
      toast.error('Primero genera el reporte y asegúrate que tenga datos');
      return;
    }
    try {
      exportReporteSemanalLotesPDF(data, 'print');
    } catch (e) {
      toast.error('Error al imprimir el PDF');
    }
  };

  const totales = data?.totales || {};
  const lotes = data?.lotes || [];
  const utilidadPos = (Number(totales.utilidad) || 0) >= 0;

  // Descubrir tipos vendidos (para desglose)
  const tiposVendidos = useMemo(() => {
    const set = new Set();
    lotes.forEach((L) => Object.keys(L.vendido_por_tipo || {}).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [lotes]);

  return (
    <Card data-testid="card-reporte-semanal-lotes">
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
              <CalendarDays className="w-5 h-5" /> Reporte Semanal por Lote
            </CardTitle>
            <CardDescription className="mt-1">
              Ventas, rotos, ingresos, costos y rentabilidad por lote en el período elegido. Imprimible en PDF.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Selector de período */}
        <div className="p-4 bg-muted/40 rounded-lg space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" onClick={presetEsta} data-testid="btn-preset-esta">
              Esta semana
            </Button>
            <Button variant="outline" size="sm" onClick={presetAnterior} data-testid="btn-preset-anterior">
              Semana anterior
            </Button>
            <Button variant="ghost" size="sm" onClick={semanaAnteriorAlActual}>
              ← Anterior a la seleccionada
            </Button>
            <Button variant="ghost" size="sm" onClick={semanaSiguienteAlActual}>
              Siguiente a la seleccionada →
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
            <div>
              <Label className="text-xs">Desde</Label>
              <Input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                className="h-10 mt-1"
                data-testid="input-desde"
              />
            </div>
            <div>
              <Label className="text-xs">Hasta</Label>
              <Input
                type="date"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                className="h-10 mt-1"
                data-testid="input-hasta"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={generar} disabled={loading} className="flex-1" data-testid="btn-generar">
                {loading
                  ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Generando…</>
                  : <><RefreshCw className="w-4 h-4 mr-2" /> Generar</>}
              </Button>
            </div>
          </div>
          <div className="flex items-center justify-between gap-2 pt-1 border-t border-border/40">
            <div className="flex items-center gap-2">
              <Switch
                id="switch-comparar"
                checked={comparar}
                onCheckedChange={setComparar}
                data-testid="switch-comparar"
              />
              <Label htmlFor="switch-comparar" className="text-xs cursor-pointer select-none">
                Comparar con el período anterior
              </Label>
            </div>
            {comparar && data?.periodo_anterior && (
              <span className="text-[11px] text-muted-foreground">
                Comparando contra:{' '}
                <span className="font-mono">
                  {data.periodo_anterior.desde} → {data.periodo_anterior.hasta}
                </span>
              </span>
            )}
          </div>
        </div>

        {/* Resultado */}
        {!data ? (
          <div className="text-center py-10 text-muted-foreground">
            <CalendarDays className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">Selecciona un período y presiona <b>Generar</b> para ver el reporte.</p>
          </div>
        ) : lotes.length === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <p className="text-sm">No hay actividad en el período seleccionado.</p>
          </div>
        ) : (
          <>
            {/* KPIs del período */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-3">
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1 text-[10px] text-muted-foreground uppercase font-semibold tracking-wide">
                    <Package className="w-3 h-3" /> Vendido
                  </div>
                  {data.variacion && <VarChip pct={data.variacion.vendido_pct} />}
                </div>
                <div className="text-xl font-bold tabular-nums mt-1" style={{ fontFamily: 'Space Grotesk' }}>
                  {fmt(totales.total_vendido)}
                </div>
                <div className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1.5">
                  {fmt(totales.total_rotos)} rotos
                  {data.variacion && (<VarChip pct={data.variacion.rotos_pct} inverso />)}
                  <span>· {totales.facturas_count || 0} conduces</span>
                </div>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200/60 dark:border-emerald-900/40 rounded-lg p-3">
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1 text-[10px] text-emerald-800 dark:text-emerald-400 uppercase font-semibold tracking-wide">
                    <DollarSign className="w-3 h-3" /> Ingresos
                  </div>
                  {data.variacion && <VarChip pct={data.variacion.ingresos_pct} />}
                </div>
                <div className="text-xl font-bold tabular-nums mt-1 text-emerald-900 dark:text-emerald-100" style={{ fontFamily: 'Space Grotesk' }}>
                  {fmtMoney(totales.total_ingresos)}
                </div>
                {data.totales_anteriores && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Antes: {fmtMoney(data.totales_anteriores.total_ingresos)}
                  </div>
                )}
              </div>
              <div className="bg-red-50 dark:bg-red-950/20 border border-red-200/60 dark:border-red-900/40 rounded-lg p-3">
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1 text-[10px] text-red-800 dark:text-red-400 uppercase font-semibold tracking-wide">
                    <DollarSign className="w-3 h-3" /> Costos
                  </div>
                  {data.variacion && <VarChip pct={data.variacion.costos_pct} inverso />}
                </div>
                <div className="text-xl font-bold tabular-nums mt-1 text-red-900 dark:text-red-100" style={{ fontFamily: 'Space Grotesk' }}>
                  {fmtMoney(totales.total_costos)}
                </div>
                {data.totales_anteriores && (
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    Antes: {fmtMoney(data.totales_anteriores.total_costos)}
                  </div>
                )}
              </div>
              <div
                className={`rounded-lg p-3 border ${
                  utilidadPos
                    ? 'bg-emerald-100/60 dark:bg-emerald-950/30 border-emerald-300/60 dark:border-emerald-900/60'
                    : 'bg-red-100/60 dark:bg-red-950/30 border-red-300/60 dark:border-red-900/60'
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <div className="flex items-center gap-1 text-[10px] uppercase font-semibold tracking-wide">
                    {utilidadPos ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                    Utilidad
                  </div>
                  {data.variacion && <VarChip pct={data.variacion.utilidad_pct} />}
                </div>
                <div
                  className={`text-xl font-bold tabular-nums mt-1 ${
                    utilidadPos ? 'text-emerald-800 dark:text-emerald-200' : 'text-red-800 dark:text-red-200'
                  }`}
                  style={{ fontFamily: 'Space Grotesk' }}
                >
                  {fmtMoney(totales.utilidad)}
                </div>
                <div className="text-[10px] mt-0.5 flex items-center gap-1.5">
                  ROI: {fmtPct(totales.roi_pct)}
                  {data.variacion && (<VarChip pct={data.variacion.roi_delta_pp} esDelta />)}
                </div>
              </div>
            </div>

            {/* Botones exportar */}
            <div className="flex flex-wrap gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={imprimirPDF} data-testid="btn-imprimir-pdf">
                <Printer className="w-4 h-4 mr-2" /> Imprimir PDF
              </Button>
              <Button size="sm" onClick={descargarPDF} data-testid="btn-descargar-pdf">
                <FileDown className="w-4 h-4 mr-2" /> Descargar PDF
              </Button>
            </div>

            {/* Tabla resumen por lote */}
            <div className="rounded-lg border border-border overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Lote</TableHead>
                    <TableHead className="text-right">Gallinas</TableHead>
                    <TableHead className="text-right">Producido</TableHead>
                    <TableHead className="text-right">Vendido</TableHead>
                    <TableHead className="text-right">Rotos</TableHead>
                    <TableHead className="text-right">Ingresos</TableHead>
                    <TableHead className="text-right">Costos</TableHead>
                    <TableHead className="text-right">Utilidad</TableHead>
                    <TableHead className="text-right">ROI</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lotes.map((L) => {
                    const utilPos = (Number(L.utilidad) || 0) >= 0;
                    const V = L._variacion || {};
                    const sinActividadActual = !!L._sin_actividad_actual;
                    return (
                      <TableRow
                        key={L.lote.id}
                        data-testid={`row-lote-${L.lote.id}`}
                        className={sinActividadActual ? 'opacity-70 bg-muted/20' : ''}
                      >
                        <TableCell className="font-semibold">
                          <div className="flex items-center gap-1.5">
                            {formatLoteLabel(L.lote)}
                            {sinActividadActual && (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 h-4">
                                sin actividad
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">{fmt(L.gallinas_actuales)}</TableCell>
                        <TableCell className="text-right tabular-nums">
                          <div className="flex items-center justify-end gap-1.5">
                            {fmt(L.total_producido)}
                            {comparar && V.producido_pct !== undefined && <VarChip pct={V.producido_pct} />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <div className="flex items-center justify-end gap-1.5">
                            {fmt(L.total_vendido)}
                            {comparar && V.vendido_pct !== undefined && <VarChip pct={V.vendido_pct} />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-red-700 dark:text-red-400">
                          <div className="flex items-center justify-end gap-1.5">
                            {fmt(L.total_rotos)}
                            {comparar && V.rotos_pct !== undefined && <VarChip pct={V.rotos_pct} inverso />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400 font-medium">
                          <div className="flex items-center justify-end gap-1.5">
                            {fmtMoney(L.total_ingresos)}
                            {comparar && V.ingresos_pct !== undefined && <VarChip pct={V.ingresos_pct} />}
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          <div className="flex items-center justify-end gap-1.5">
                            {fmtMoney(L.total_costos)}
                            {comparar && V.costos_pct !== undefined && <VarChip pct={V.costos_pct} inverso />}
                          </div>
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums font-bold ${
                            utilPos ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'
                          }`}
                        >
                          <div className="flex items-center justify-end gap-1.5">
                            {fmtMoney(L.utilidad)}
                            {comparar && V.utilidad_pct !== undefined && <VarChip pct={V.utilidad_pct} />}
                          </div>
                        </TableCell>
                        <TableCell
                          className={`text-right tabular-nums font-bold ${
                            (Number(L.roi_pct) || 0) >= 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-400'
                          }`}
                        >
                          <div className="flex items-center justify-end gap-1.5">
                            {fmtPct(L.roi_pct)}
                            {comparar && V.roi_delta_pp !== undefined && <VarChip pct={V.roi_delta_pp} esDelta />}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {/* Fila de totales */}
                  <TableRow className="bg-muted/50 font-bold">
                    <TableCell>TOTAL</TableCell>
                    <TableCell />
                    <TableCell className="text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {fmt(totales.total_producido)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.producido_pct} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {fmt(totales.total_vendido)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.vendido_pct} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {fmt(totales.total_rotos)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.rotos_pct} inverso />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {fmtMoney(totales.total_ingresos)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.ingresos_pct} />}
                      </div>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <div className="flex items-center justify-end gap-1.5">
                        {fmtMoney(totales.total_costos)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.costos_pct} inverso />}
                      </div>
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${utilidadPos ? 'text-emerald-700' : 'text-red-700'}`}>
                      <div className="flex items-center justify-end gap-1.5">
                        {fmtMoney(totales.utilidad)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.utilidad_pct} />}
                      </div>
                    </TableCell>
                    <TableCell className={`text-right tabular-nums ${(Number(totales.roi_pct) || 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                      <div className="flex items-center justify-end gap-1.5">
                        {fmtPct(totales.roi_pct)}
                        {comparar && data.variacion && <VarChip pct={data.variacion.roi_delta_pp} esDelta />}
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </div>

            {/* Desglose por tipo (si hay ventas) */}
            {tiposVendidos.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Ventas por tipo de huevo
                </h4>
                <div className="rounded-lg border border-border overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Lote</TableHead>
                        {tiposVendidos.map((t) => (
                          <TableHead key={t} className="text-right capitalize">{t}</TableHead>
                        ))}
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Ingreso</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {lotes.filter((L) => L.total_vendido > 0).map((L) => (
                        <TableRow key={`d-${L.lote.id}`}>
                          <TableCell className="font-semibold">{formatLoteLabel(L.lote)}</TableCell>
                          {tiposVendidos.map((t) => (
                            <TableCell key={t} className="text-right tabular-nums">
                              {fmt(L.vendido_por_tipo?.[t] || 0)}
                            </TableCell>
                          ))}
                          <TableCell className="text-right tabular-nums font-semibold">
                            <Badge variant="outline" className="tabular-nums">
                              {fmt(L.total_vendido)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-emerald-700 dark:text-emerald-400 font-semibold">
                            {fmtMoney(L.total_ingresos)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default ReporteSemanalLotes;
